/**
 * Кнопка «Наверх»
 *
 * Показывается, только когда пользователь прокручивает страницу вверх,
 * и скрывается при прокрутке вниз или у верха страницы.
 *
 * Если на странице есть чат CarrotQuest, кнопка встаёт над его пузырём,
 * иначе — на место пузыря. От виджета ничего не требуется: если он отключён
 * или не загрузился, кнопка работает с позицией по умолчанию из CSS.
 */

// Контейнер свёрнутого чата, CarrotQuest добавляет его прямо в <body>
const CHAT_CONTAINER_ID = 'carrotquest-messenger-collapsed-container';

// Геометрия свёрнутого чата CarrotQuest (chunk-model2.js / launcher.js):
// контейнер получает margin = отступ из настроек − 10px, внутри — блок
// 80×80 (#icon-container) с пузырём по центру
const CHAT_MARGIN_CORRECTION = -10;
const CHAT_ICON_HALF         = 40;

// Наш сдвиг #icon-container к краю: -mr-3 -mb-3 в resources/css/services/carrotquest.css
const THEME_ICON_OFFSET = -12;

export default () => ({

    is_visible: false,

    last_scroll_top: 0,

    // Путь, пройденный в текущем направлении (< 0 — вверх, > 0 — вниз)
    scroll_distance: 0,

    // Сколько нужно непрерывно прокрутить, чтобы сменить состояние:
    // гасит дрожание от мелких движений и «резинку» iOS
    direction_threshold: 48,

    // Есть ли на странице пузырь чата, над которым нужно встать
    has_chat: false,

    // Расстояние от правого / нижнего края до центра пузыря чата, px;
    // null — значение по умолчанию из CSS
    chat_center_x: null,
    chat_center_y: null,

    init() {
        this.last_scroll_top = this.getScrollTop();

        window.addEventListener('scroll', () => this.handleScroll(), {passive: true});

        // Чат грузится асинхронно и может появиться в любой момент (или
        // не появиться вовсе), поэтому следим за детьми <body>. Наблюдение
        // только за прямыми потомками, без subtree, — это дёшево.
        this.syncChat();
        new MutationObserver(() => this.syncChat()).observe(document.body, {childList: true});
    },

    /**
     * Стили кнопки: CSS-переменные с положением пузыря чата
     */
    chatStyle() {
        const style = {};

        if (this.chat_center_x !== null) {
            style['--chat-center-x'] = this.chat_center_x + 'px';
        }

        if (this.chat_center_y !== null) {
            style['--chat-center-y'] = this.chat_center_y + 'px';
        }

        return style;
    },

    /**
     * Сверка с состоянием чата CarrotQuest
     */
    syncChat() {
        const has_container = !!document.getElementById(CHAT_CONTAINER_ID);
        const settings      = window.carrotquest?.data?.app?.settings;

        // Положение чата — в правом нижнем углу (по умолчанию и как сейчас
        // настроено). Если чат перенесут в другой угол, кнопка останется
        // в правом нижнем, где ей уже ничто не мешает.
        const position = settings?.messenger_position ?? 'right_bottom';

        this.has_chat = has_container && position === 'right_bottom';

        if (!this.has_chat) {
            return;
        }

        // Отступы чата берутся из настроек виджета, отдельно для мобильных;
        // признак мобильного — тот же, что у самого CarrotQuest
        const indent = window.carrotquest?.isMobile
            ? settings?.messenger_mobile_indent
            : settings?.messenger_indent;

        // Неизвестное значение не сбрасываем: Alpine при удалении ключа из
        // x-bind:style выставляет CSS-переменной некорректное значение, и
        // кнопка теряет позицию. Последнее известное значение надёжнее.
        const center_x = this.getChatCenter(indent?.horizontal);
        const center_y = this.getChatCenter(indent?.vertical);

        if (center_x !== null) {
            this.chat_center_x = center_x;
        }

        if (center_y !== null) {
            this.chat_center_y = center_y;
        }
    },

    /**
     * Расстояние от края экрана до центра пузыря по отступу из настроек
     *
     * @param {*} indent
     * @return {number|null} null — отступ неизвестен, значение не меняется
     */
    getChatCenter(indent) {
        if (typeof indent !== 'number' || !Number.isFinite(indent)) {
            return null;
        }

        return indent + CHAT_MARGIN_CORRECTION + THEME_ICON_OFFSET + CHAT_ICON_HALF;
    },

    getScrollTop() {
        return Math.max(0, document.documentElement.scrollTop);
    },

    handleScroll() {
        const scroll_top = this.getScrollTop();
        const delta      = scroll_top - this.last_scroll_top;

        this.last_scroll_top = scroll_top;

        // У верха страницы кнопка не нужна
        if (scroll_top < window.innerHeight * 0.75) {
            this.is_visible      = false;
            this.scroll_distance = 0;
            return;
        }

        // Смена направления — начинаем отсчёт заново
        if ((delta > 0 && this.scroll_distance < 0) || (delta < 0 && this.scroll_distance > 0)) {
            this.scroll_distance = 0;
        }

        this.scroll_distance += delta;

        if (this.scroll_distance <= -this.direction_threshold) {
            this.is_visible = true;
        } else if (this.scroll_distance >= this.direction_threshold) {
            this.is_visible = false;
        }
    },

    scrollToTop() {
        const reduce_motion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        window.scrollTo({top: 0, behavior: reduce_motion ? 'auto' : 'smooth'});

        // Кнопка скроется у верха страницы — не оставляем на ней фокус
        this.$root.blur();
    },
});
